import * as t from 'io-ts';

import {enforceType} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {randomString} from '../../../libs/shared/helpers/src/utilities/stringHelpers';
import {makeEnumRuntimeType} from '../../models/api/apiTypes';

/*
 * App panel ID.
 */

interface AppPanelIdBrand {
  readonly AppPanelId: unique symbol;
}

const appPanelIdBase = 'app';
export const RAppPanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, AppPanelIdBrand> => isTopLevelPanelId(appPanelIdBase, id),
  'AppPanelId',
);

export type AppPanelId = t.TypeOf<typeof RAppPanelId>;
export function makeAppPanelId(): AppPanelId {
  return enforceType(RAppPanelId, makeTopLevelId(appPanelIdBase));
}

export function tryFindAppPanelIdInPanelId(panelId: NewPanelId): AppPanelId | undefined {
  const appPanelId = extractAppPanelId(panelId);
  if (!RAppPanelId.is(appPanelId)) {
    return undefined;
  }

  return appPanelId;
}

export function findAppPanelIdInContentPanelId(
  panelId: InboxesPanelId | ContactsManagerPanelId | SettingsPanelId,
) {
  const [appPanelId] = splitPanelId(panelId);
  return enforceType(RAppPanelId, appPanelId);
}

/*
 * Inboxes panel ID.
 */

interface InboxesPanelIdBrand {
  readonly InboxesPanelId: unique symbol;
}

const inboxesPanelIdBase = 'inboxes';
export const RInboxesPanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, InboxesPanelIdBrand> => {
    const [appPanelId, inboxesPanelId] = splitPanelId(id);
    return RAppPanelId.is(appPanelId) && inboxesPanelId === inboxesPanelIdBase;
  },
  'InboxesPanelId',
);

export type InboxesPanelId = t.TypeOf<typeof RInboxesPanelId>;
export function makeInboxesPanelId(appPanelId: AppPanelId): InboxesPanelId {
  return enforceType(RInboxesPanelId, combinePanelIds(appPanelId, inboxesPanelIdBase));
}

export function findInboxesPanelIdInPanelId(panelId: CellsPanelId) {
  const [inboxesPanelId] = splitPanelId(panelId);
  return enforceType(RInboxesPanelId, inboxesPanelId);
}

export function tryFindInboxesPanelIdInPanelId(panelId: CellsPanelId | ViewerPanelId) {
  if (isTopLevelPanelId(viewerPanelIdBase, panelId)) {
    return undefined;
  }

  const [parentPanelId] = splitPanelId(panelId);
  if (RAppPanelId.is(parentPanelId)) {
    return undefined;
  }

  return enforceType(RInboxesPanelId, parentPanelId);
}

/*
 * Calendars panel ID.
 */

interface CalendarsPanelIdBrand {
  readonly CalendarsPanelId: unique symbol;
}

const calendarsPanelIdBase = 'calendar';
export const RCalendarsPanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, CalendarsPanelIdBrand> => {
    if (isTopLevelPanelId(calendarsPanelIdBase, id)) {
      return true;
    }

    const [parentPanelId, calendarsPanelId] = splitPanelId(id);
    if (calendarsPanelId !== calendarsPanelIdBase) {
      return false;
    }

    return (
      RAppPanelId.is(parentPanelId) ||
      RViewerPanelId.is(parentPanelId) ||
      RSchedulingPanelId.is(parentPanelId) ||
      REmlPanelId.is(parentPanelId) ||
      RAccountDetailsPanelId.is(parentPanelId) ||
      RContactDetailsPanelId.is(parentPanelId) ||
      RMessagePanelId.is(parentPanelId)
    );
  },
  'CalendarsPanelId',
);

export type CalendarsPanelId = t.TypeOf<typeof RCalendarsPanelId>;
export function makeCalendarsPanelId(
  parentPanelId?:
    | AppPanelId
    | ViewerPanelId
    | SchedulingPanelId
    | EmlPanelId
    | AccountDetailsPanelId
    | ContactDetailsPanelId
    | MessagePanelId,
): CalendarsPanelId {
  // Calendar in an App panel, in P4, or in the Scheduling panel.
  if (parentPanelId) {
    return enforceType(RCalendarsPanelId, combinePanelIds(parentPanelId, calendarsPanelIdBase));
  }

  // Popup calendar.
  return enforceType(RCalendarsPanelId, makeTopLevelId(calendarsPanelIdBase));
}

/** Decides whether this is a full screen calendar. */
export function isAppCalendarsPanelId(id: CalendarsPanelId): boolean {
  const [parentPanelId, calendarsPanelId] = splitPanelId(id);
  return RAppPanelId.is(parentPanelId) && calendarsPanelId === calendarsPanelIdBase;
}

/** Decides whether this is a P4 calendar. */
export function isCalendarsSidePanelId(panelId: CalendarsPanelId): boolean {
  return Boolean(tryFindDockedViewerPanelId(panelId));
}

/*
 * Analytics panel ID.
 */

interface AnalyticsPanelIdBrand {
  readonly AnalyticsPanelId: unique symbol;
}

const analyticsPanelIdBase = 'analytics';
export const RAnalyticsPanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, AnalyticsPanelIdBrand> => {
    const [appPanelId, analyticsPanelId] = splitPanelId(id);
    return RAppPanelId.is(appPanelId) && analyticsPanelId === analyticsPanelIdBase;
  },
  'AnalyticsPanelId',
);

export type AnalyticsPanelId = t.TypeOf<typeof RAnalyticsPanelId>;
export function makeAnalyticsPanelId(appPanelId: AppPanelId): AnalyticsPanelId {
  return enforceType(RAnalyticsPanelId, combinePanelIds(appPanelId, analyticsPanelIdBase));
}

/*
 * Cells panel ID.
 */

interface CellsPanelIdBrand {
  readonly CellsPanelId: unique symbol;
}

const cellsPanelIdBase = 'cells';
export const RCellsPanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, CellsPanelIdBrand> => {
    const [inboxesPanelId, cellsPanelId] = splitPanelId(id);
    return RInboxesPanelId.is(inboxesPanelId) && cellsPanelId === cellsPanelIdBase;
  },
  'CellsPanelId',
);

export type CellsPanelId = t.TypeOf<typeof RCellsPanelId>;
export function makeCellsPanelId(inboxesPanelId: InboxesPanelId): CellsPanelId {
  return enforceType(RCellsPanelId, combinePanelIds(inboxesPanelId, cellsPanelIdBase));
}

/*
 * Viewer panel ID.
 */

interface ViewerPanelIdBrand {
  readonly ViewerPanelId: unique symbol;
}

const viewerPanelIdBase = 'viewer';
export const RViewerPanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, ViewerPanelIdBrand> => {
    if (isTopLevelPanelId(viewerPanelIdBase, id)) {
      return true;
    }

    const [parentPanelId, viewerPanelId] = splitPanelId(id);
    return (
      (RAppPanelId.is(parentPanelId) || RInboxesPanelId.is(parentPanelId)) &&
      viewerPanelId === viewerPanelIdBase
    );
  },
  'ViewerPanelId',
);

export type ViewerPanelId = t.TypeOf<typeof RViewerPanelId>;
export function makeViewerPanelId(parentPanelId?: AppPanelId | InboxesPanelId): ViewerPanelId {
  // Visitor viewer or docked viewer.
  if (parentPanelId) {
    return enforceType(RViewerPanelId, combinePanelIds(parentPanelId, viewerPanelIdBase));
  }

  // Popup viewer.
  return enforceType(RViewerPanelId, makeTopLevelId(viewerPanelIdBase));
}

function isDockedViewerPanelId(panelId: ViewerPanelId): boolean {
  const [inboxesPanelId] = splitPanelId(panelId);
  return RInboxesPanelId.is(inboxesPanelId);
}

export function tryFindDockedViewerPanelId(
  panelId: CalendarsPanelId | ContactDetailsPanelId | AccountDetailsPanelId,
): ViewerPanelId | undefined {
  const [parentPanelId] = splitPanelId(panelId);
  if (!RViewerPanelId.is(parentPanelId) || !isDockedViewerPanelId(parentPanelId)) {
    return undefined;
  }

  return enforceType(RViewerPanelId, parentPanelId);
}

/*
 * Account Details panel ID.
 */

interface AccountDetailsPanelIdBrand {
  readonly AccountDetailsPanelId: unique symbol;
}

const accountDetailsPanelIdBase = 'accountDetails';
export const RAccountDetailsPanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, AccountDetailsPanelIdBrand> => {
    if (isTopLevelPanelId(accountDetailsPanelIdBase, id)) {
      return true;
    }

    const [parentPanelId, detailsPanelId] = splitPanelId(id);
    return (
      (RViewerPanelId.is(parentPanelId) || RContactsManagerPanelId.is(parentPanelId)) &&
      detailsPanelId === accountDetailsPanelIdBase
    );
  },
  'AccountDetailsPanelId',
);

export type AccountDetailsPanelId = t.TypeOf<typeof RAccountDetailsPanelId>;
export function makeAccountDetailsPanelId(
  parentPanelId?: ViewerPanelId | ContactsManagerPanelId,
): AccountDetailsPanelId {
  // Account Details is in an Inboxes Viewer side panel (P4), or in a Contacts Manager side panel.
  if (parentPanelId) {
    return enforceType(RAccountDetailsPanelId, combinePanelIds(parentPanelId, accountDetailsPanelIdBase));
  }

  // Popup (popped out) account details.
  return enforceType(RAccountDetailsPanelId, makeTopLevelId(accountDetailsPanelIdBase));
}

/*
 * Contact Details panel ID.
 */

interface ContactDetailsPanelIdBrand {
  readonly ContactDetailsPanelId: unique symbol;
}

const contactDetailsPanelIdBase = 'contactDetails';
export const RContactDetailsPanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, ContactDetailsPanelIdBrand> => {
    if (isTopLevelPanelId(contactDetailsPanelIdBase, id)) {
      return true;
    }

    const [parentPanelId, detailsPanelId] = splitPanelId(id);
    return (
      (RViewerPanelId.is(parentPanelId) || RContactsManagerPanelId.is(parentPanelId)) &&
      detailsPanelId === contactDetailsPanelIdBase
    );
  },
  'ContactDetailsPanelId',
);

export type ContactDetailsPanelId = t.TypeOf<typeof RContactDetailsPanelId>;
export function makeContactDetailsPanelId(
  parentPanelId?: ViewerPanelId | ContactsManagerPanelId,
): ContactDetailsPanelId {
  // Contact Details is in an Inboxes Viewer side panel (P4), or in a Contacts Manager side panel.
  if (parentPanelId) {
    return enforceType(RContactDetailsPanelId, combinePanelIds(parentPanelId, contactDetailsPanelIdBase));
  }

  // Popup (popped out) account details.
  return enforceType(RContactDetailsPanelId, makeTopLevelId(contactDetailsPanelIdBase));
}

/*
 * Contacts Manager panel ID.
 */

interface ContactsManagerPanelIdBrand {
  readonly ContactsManagerPanelId: unique symbol;
}

const contactsManagerPanelIdBase = 'contactsManager';
export const RContactsManagerPanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, ContactsManagerPanelIdBrand> => {
    const [appPanelId, managerPanelId] = splitPanelId(id);
    return RAppPanelId.is(appPanelId) && managerPanelId === contactsManagerPanelIdBase;
  },
  'ContactsManagerPanelId',
);

export type ContactsManagerPanelId = t.TypeOf<typeof RContactsManagerPanelId>;
export function makeContactsManagerPanelId(appPanelId: AppPanelId): ContactsManagerPanelId {
  return enforceType(RContactsManagerPanelId, combinePanelIds(appPanelId, contactsManagerPanelIdBase));
}

/*
 * Contact Card panel ID.
 */

interface ContactCardPanelIdBrand {
  readonly ContactCardPanelId: unique symbol;
}

const contactCardPanelIdBase = 'contactCard';
export const RContactCardPanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, ContactCardPanelIdBrand> => {
    const [parentPanelId, contactCardPanelId] = splitPanelId(id);
    return (
      (RViewerPanelId.is(parentPanelId) ||
        RMessagePanelId.is(parentPanelId) ||
        REmlPanelId.is(parentPanelId) ||
        RCalendarsPanelId.is(parentPanelId) ||
        RAccountDetailsPanelId.is(parentPanelId) ||
        RContactDetailsPanelId.is(parentPanelId)) &&
      contactCardPanelId === contactCardPanelIdBase
    );
  },
  'ContactCardPanelId',
);

export type ContactCardPanelId = t.TypeOf<typeof RContactCardPanelId>;
export function makeContactCardPanelId(
  panelId:
    | ViewerPanelId
    | MessagePanelId
    | EmlPanelId
    | CalendarsPanelId
    | AccountDetailsPanelId
    | ContactDetailsPanelId,
): ContactCardPanelId {
  return enforceType(RContactCardPanelId, combinePanelIds(panelId, contactCardPanelIdBase));
}

/*
 * CRM Preview Panel Id.
 */

interface CrmPreviewPanelIdBrand {
  readonly CrmPreviewPanelId: unique symbol;
}

const crmPreviewPanelIdBase = 'crmPreview';
export const RCrmPreviewPanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, CrmPreviewPanelIdBrand> => {
    const [parentPanelId, accountCrmPanelId] = splitPanelId(id);
    return (
      (RAccountDetailsPanelId.is(parentPanelId) ||
        RContactsManagerPanelId.is(parentPanelId) ||
        RContactDetailsPanelId.is(parentPanelId) ||
        RSettingsPanelId.is(parentPanelId)) &&
      accountCrmPanelId === crmPreviewPanelIdBase
    );
  },
  'CrmPreviewPanelId',
);

export type CrmPreviewPanelId = t.TypeOf<typeof RCrmPreviewPanelId>;
export function makeCrmPreviewPanelId(
  panelId: AccountDetailsPanelId | ContactsManagerPanelId | SettingsPanelId | ContactDetailsPanelId,
): CrmPreviewPanelId {
  return enforceType(RCrmPreviewPanelId, combinePanelIds(panelId, crmPreviewPanelIdBase));
}

/*
 * Composer panel ID.
 */

interface ComposerPanelIdBrand {
  readonly ComposerPanelId: unique symbol;
}

const composerPanelIdBase = 'composer';
export const RComposerPanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, ComposerPanelIdBrand> => {
    if (isTopLevelPanelId(composerPanelIdBase, id)) {
      return true;
    }

    const [appPanelId, composerPanelId] = splitPanelId(id);
    return RAppPanelId.is(appPanelId) && composerPanelId === composerPanelIdBase;
  },
  'ComposerPanelId',
);

export type ComposerPanelId = t.TypeOf<typeof RComposerPanelId>;
export function makeComposerPanelId(appPanelId?: AppPanelId): ComposerPanelId {
  // Standalone composer (/compose route).
  if (appPanelId) {
    return enforceType(RComposerPanelId, combinePanelIds(appPanelId, composerPanelIdBase));
  }

  // Popup composer.
  return enforceType(RComposerPanelId, makeTopLevelId(composerPanelIdBase));
}

/*
 * Scheduling panel ID.
 */

interface SchedulingPanelIdBrand {
  readonly SchedulingPanelId: unique symbol;
}

const schedulingPanelIdBase = 'scheduling';
export const RSchedulingPanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, SchedulingPanelIdBrand> => isTopLevelPanelId(schedulingPanelIdBase, id),
  'SchedulingPanelId',
);

export type SchedulingPanelId = t.TypeOf<typeof RSchedulingPanelId>;
export function makeSchedulingPanelId(): SchedulingPanelId {
  return enforceType(RSchedulingPanelId, makeTopLevelId(schedulingPanelIdBase));
}

/*
 * Message panel ID.
 */

interface MessagePanelIdBrand {
  readonly MessagePanelId: unique symbol;
}

const messagePanelIdBase = 'message';
export const RMessagePanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, MessagePanelIdBrand> => isTopLevelPanelId(messagePanelIdBase, id),
  'MessagePanelId',
);

export type MessagePanelId = t.TypeOf<typeof RMessagePanelId>;
export function makeMessagePanelId(): MessagePanelId {
  return enforceType(RMessagePanelId, makeTopLevelId(messagePanelIdBase));
}

/*
 * EML panel ID.
 */

interface EmlPanelIdBrand {
  readonly EmlPanelId: unique symbol;
}

const emlPanelIdBase = 'eml';
export const REmlPanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, EmlPanelIdBrand> => isTopLevelPanelId(emlPanelIdBase, id),
  'EmlPanelId',
);

export type EmlPanelId = t.TypeOf<typeof REmlPanelId>;
export function makeEmlPanelId(): EmlPanelId {
  return enforceType(REmlPanelId, makeTopLevelId(emlPanelIdBase));
}

/*
 * Settings panel ID.
 */

interface SettingsPanelIdBrand {
  readonly SettingsPanelId: unique symbol;
}

const settingsPanelIdBase = 'settings';
export const RSettingsPanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, SettingsPanelIdBrand> => {
    const [appPanelId, settingsPanelId] = splitPanelId(id);
    return RAppPanelId.is(appPanelId) && settingsPanelId === settingsPanelIdBase;
  },
  'SettingsPanelId',
);

export type SettingsPanelId = t.TypeOf<typeof RSettingsPanelId>;
export function makeSettingsPanelId(appPanelId: AppPanelId): SettingsPanelId {
  return enforceType(RSettingsPanelId, combinePanelIds(appPanelId, settingsPanelIdBase));
}

/*
 * Old Front panel ID.
 */

interface LegacyPanelIdBrand {
  readonly LegacyPanelId: unique symbol;
}

const legacyPanelIdBase = 'legacy';
export const RLegacyPanelId = t.brand(
  t.string,
  (id): id is t.Branded<string, LegacyPanelIdBrand> => {
    const [appPanelId, legacyPanelId] = splitPanelId(id);
    return RAppPanelId.is(appPanelId) && legacyPanelId === legacyPanelIdBase;
  },
  'LegacyPanelId',
);

export type LegacyPanelId = t.TypeOf<typeof RLegacyPanelId>;
export function makeLegacyPanelId(appPanelId: AppPanelId): LegacyPanelId {
  return enforceType(RLegacyPanelId, combinePanelIds(appPanelId, legacyPanelIdBase));
}

/*
 * Combined panel ID.
 */

export const RPanelId = t.union([
  RAppPanelId,
  RInboxesPanelId,
  RCalendarsPanelId,
  RAnalyticsPanelId,
  RCellsPanelId,
  RViewerPanelId,
  RAccountDetailsPanelId,
  RContactDetailsPanelId,
  RContactsManagerPanelId,
  RContactCardPanelId,
  RComposerPanelId,
  RSchedulingPanelId,
  RMessagePanelId,
  REmlPanelId,
  RSettingsPanelId,
  RLegacyPanelId,
  RCrmPreviewPanelId,
]);
export type NewPanelId =
  | AppPanelId
  | InboxesPanelId
  | CellsPanelId
  | ViewerPanelId
  | MessagePanelId
  | ComposerPanelId
  | CalendarsPanelId
  | AnalyticsPanelId
  | EmlPanelId
  | ContactDetailsPanelId
  | ContactsManagerPanelId
  | ContactCardPanelId
  | SettingsPanelId
  | AccountDetailsPanelId
  | SchedulingPanelId
  | LegacyPanelId
  | CrmPreviewPanelId;

export type ContactOrAccountDetailsPanelId = ContactDetailsPanelId | AccountDetailsPanelId;

/*
 * Base panel.
 */

export enum PanelTypesEnum {
  APP = 'APP',
  INBOXES = 'INBOXES',
  CELLS = 'CELLS',
  VIEWER = 'VIEWER',
  MESSAGE = 'MESSAGE',
  COMPOSER = 'COMPOSER',
  CALENDARS = 'CALENDARS',
  ANALYTICS = 'ANALYTICS',
  EML = 'EML',
  CONTACT_CARD = 'CONTACT_CARD',
  CONTACT_DETAILS = 'CONTACT_DETAILS',
  CONTACTS_MANAGER = 'CONTACTS_MANAGER',
  SETTINGS = 'SETTINGS',
  LEGACY = 'LEGACY',
  ACCOUNT_DETAILS = 'ACCOUNT_DETAILS',
  SCHEDULING = 'SCHEDULING',
  CRM_PREVIEW = 'CRM_PREVIEW',
}

export const RPanelStateBase = t.readonly(
  t.intersection([
    t.type({
      id: RPanelId,
      type: makeEnumRuntimeType(PanelTypesEnum),
      sequence: t.number,
    }),
    t.partial({
      popupAppPanelId: RAppPanelId,
      shouldForcePopout: t.boolean,
    }),
  ]),
);

/*
 * Panel ID helpers.
 */

export const panelIdSeparator = '-';
function combinePanelIds(...panelIds: ReadonlyArray<string>) {
  return panelIds.join(panelIdSeparator);
}

export function splitPanelId(panelId: string): [string, string] {
  const splitIndex = panelId.lastIndexOf(panelIdSeparator);
  return [panelId.substring(0, splitIndex), panelId.substring(splitIndex + 1)];
}

const topLevelPanelIdLength = 32;
// TODO: Rename the non-deterministic/random panel ID generators, so we don't accidentally use them in pure functions.
function makeTopLevelId(prefix: string) {
  return prefix + randomString(topLevelPanelIdLength);
}

function isTopLevelPanelId(prefix: string, panelId: string) {
  return panelId.startsWith(prefix) && panelId.length === prefix.length + topLevelPanelIdLength;
}

function extractAppPanelId(panelId: string) {
  const splitIndex = panelId.indexOf(panelIdSeparator);
  return splitIndex < 0 ? panelId : panelId.substring(0, splitIndex);
}
